/**
 * CurrentPlanCard Component
 * Shows current subscription plan details and usage
 *
 * Features:
 * - Plan tier and status badge
 * - "Manage Billing" button (opens Stripe Customer Portal)
 * - Usage progress bars (SKU count, SmartImport runs)
 * - Next billing date and amount
 * - Feature highlights (collapsible)
 */

import { useEffect, useRef } from 'react'
import { Card, Button, Space, Typography, Tag, Collapse } from 'antd'
import { CreditCardOutlined, CheckOutlined } from '@ant-design/icons'
import { useStripeCheckout } from '@/hooks/useStripeCheckout'
import { UsageProgressBar } from '@/components/billing/UsageProgressBar'
import { errorService } from '@/services/errorService'
import { PLAN_TIERS, STATUS_COLORS, STATUS_TEXT } from '@/types/billing.types'
import type { CurrentPlan, UsageMetrics, PlanTier } from '@/types/billing.types'

/** Valid tier values that exist in PLAN_TIERS */
const VALID_TIERS: PlanTier[] = ['free', 'growth', 'professional', 'business']

const { Text, Paragraph } = Typography

interface Props {
  currentPlan: CurrentPlan
  usage?: UsageMetrics
}

export function CurrentPlanCard({ currentPlan, usage }: Props) {
  const { openPortal, loading } = useStripeCheckout()
  const errorShownRef = useRef(false)

  // Check if the tier is valid
  const isValidTier = VALID_TIERS.includes(currentPlan.tier)

  // Defensive: Fall back to 'free' tier if currentPlan.tier is invalid
  const planDetails = isValidTier ? PLAN_TIERS[currentPlan.tier] : PLAN_TIERS.free

  // Show error once if tier is invalid
  useEffect(() => {
    if (!isValidTier && !errorShownRef.current) {
      errorShownRef.current = true
      errorService.showError(
        'Subscription Status Issue',
        'We could not verify your current subscription tier, so you are temporarily using free tier access.\n\n' +
        'You can continue working - your data is safe and all free features are available. However, some features you normally use may be limited until this is resolved.\n\n' +
        'This is likely a quick fix on our end. Please use the button below to copy the error details and send them to support@rocketpricing.com. We will get this sorted out promptly.',
        [
          {
            code: 'INVALID_SUBSCRIPTION_TIER',
            message: `Unrecognized subscription tier: "${currentPlan.tier}"`,
            details: {
              receivedTier: currentPlan.tier,
              validTiers: VALID_TIERS,
              fallbackTier: 'free'
            }
          }
        ]
      )
    }
  }, [isValidTier, currentPlan.tier])

  // Format next billing date
  const nextBillingText = currentPlan.currentPeriodEnd
    ? `${new Date(currentPlan.currentPeriodEnd * 1000).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })} ($${planDetails.priceMonthly}.00)`
    : 'None (Free plan)'

  // Contextual usage tip
  const usageTip = usage && usage.skuCount.percentage > 70
    ? `You're using ${Math.round(usage.skuCount.percentage)}% of your SKU limit. ${
        usage.skuCount.percentage > 90
          ? 'Upgrade soon to avoid hitting the limit.'
          : 'Upgrade for more capacity if you need it.'
      }`
    : null

  return (
    <Card
      title="Your Current Plan"
      extra={
        <Button
          type="primary"
          icon={<CreditCardOutlined />}
          onClick={openPortal}
          loading={loading}
        >
          Manage Billing
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Plan header */}
        <div>
          <Space align="center">
            <Text strong style={{ fontSize: '18px' }}>
              {planDetails.displayName} Plan
            </Text>
            <Tag color={STATUS_COLORS[currentPlan.status]}>
              {STATUS_TEXT[currentPlan.status]}
            </Tag>
          </Space>
        </div>

        {/* Usage visualization */}
        {usage && (
          <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px' }}>
            <Text strong style={{ marginBottom: '12px', display: 'block' }}>
              Usage This Month
            </Text>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* SKU count */}
              <UsageProgressBar
                label="SKUs"
                current={usage.skuCount.current}
                limit={usage.skuCount.limit}
                percentage={usage.skuCount.percentage}
                showWarning={usage.skuCount.percentage > 90}
              />

              {/* SmartImport runs */}
              <UsageProgressBar
                label="SmartImport Runs"
                current={usage.smartimportRuns.current}
                limit={usage.smartimportRuns.limit}
                percentage={usage.smartimportRuns.percentage}
                unlimited={usage.smartimportRuns.unlimited}
              />

              {/* Contextual tip */}
              {usageTip && (
                <Paragraph
                  type="secondary"
                  style={{
                    margin: '8px 0 0 0',
                    fontSize: '13px',
                    background: usage.skuCount.percentage > 90 ? '#fff7e6' : '#e6f7ff',
                    padding: '8px 12px',
                    borderRadius: '4px'
                  }}
                >
                  {usageTip}
                </Paragraph>
              )}
            </Space>
          </div>
        )}

        {/* Next billing date */}
        <div>
          <Text type="secondary">Next billing date: </Text>
          <Text>{nextBillingText}</Text>
        </div>

        {/* Feature highlights (collapsible) */}
        <Collapse
          ghost
          items={[
            {
              key: 'features',
              label: 'View All Features',
              children: (
                <Space direction="vertical">
                  {planDetails.features.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CheckOutlined style={{ color: '#52c41a' }} />
                      <Text>{feature}</Text>
                    </div>
                  ))}
                </Space>
              )
            }
          ]}
        />

        {/* Upgrade button (show if not on highest tier) */}
        {currentPlan.tier !== 'business' && (
          <Button type="link" onClick={() => {
            // Scroll to upgrade options section
            document.getElementById('upgrade-options')?.scrollIntoView({ behavior: 'smooth' })
          }}>
            Upgrade Plan →
          </Button>
        )}
      </Space>
    </Card>
  )
}
